import { get } from 'lodash';
import { Children, Fragment, isValidElement, ReactNode } from 'react';

import { FieldRule, ItemFieldSpec } from './types';

/**
 * The slice of a Mantine form this module needs.
 *
 * Structural rather than an import so the framework does not depend on a form library at all - a
 * feature could pass any object with these three members.
 */
export interface PolicyForm {
  getInputProps: (path: string, options?: any) => Record<string, any>;
  setFieldValue: (path: string, value: any) => void;
  values: unknown;
}

/** Props every policy field takes, regardless of feature. */
export interface PolicyFieldBaseProps {
  path: string;
  rule: FieldRule;
  form?: PolicyForm;
  /** Extra disable reason the policy cannot know, e.g. a value that changed in the form. */
  disabledWhen?: boolean;
  /**
   * Extra hide reason the policy cannot express: gates that depend on an entity or form *value*
   * rather than on a context axis. Those cannot live in a Selector, so they arrive here.
   */
  hiddenWhen?: boolean;
  /** Opt out of the resolved `disabled` for the few fields that ignore it. */
  ignoreReadOnly?: boolean;
}

/**
 * Whether a policy field will render anything.
 *
 * The SINGLE definition of that question. `PolicyField` uses it to decide whether to return null,
 * and `hasVisibleChild` uses it to decide whether a row is empty. Two copies would drift the first
 * time a third hide reason is added, and the failure would be silent - a row rendering empty, or
 * swallowing a field that should show.
 */
export const willRender = ({ rule, hiddenWhen = false }: Pick<PolicyFieldBaseProps, 'rule'> & { hiddenWhen?: boolean }): boolean => rule.present && !hiddenWhen;

/** Looks like a rendered policy field: carries a resolved rule. */
const isPolicyFieldElement = (props: unknown): props is Pick<PolicyFieldBaseProps, 'rule'> & { hiddenWhen?: boolean } => {
  const rule = (props as { rule?: unknown })?.rule;

  return Boolean(rule) && typeof rule === 'object' && 'present' in (rule as object);
};

/**
 * Whether any child would render - used to collapse a layout row that would otherwise be empty.
 *
 * Reads the children's PROPS, never their output: a parent cannot know what a child rendered,
 * because the child renders after it. The decision is already in the props, so this is not
 * clairvoyance - it asks the same question the child will ask itself.
 *
 * Anything that is not recognisably a policy field counts as VISIBLE. Hand-written columns carry no
 * rule, and they render whenever their own guard let them through, so the safe default is to keep
 * the row. This can only fail towards showing a row, never towards hiding content.
 */
export const hasVisibleChild = (children: ReactNode): boolean =>
  Children.toArray(children).some((child) => {
    if (!isValidElement(child)) return true; // strings and numbers are content; toArray already dropped null/false

    if (child.type === Fragment) return hasVisibleChild((child.props as { children?: ReactNode }).children);

    return isPolicyFieldElement(child.props) ? willRender(child.props) : true;
  });

export interface WidgetContext<TExtra> extends PolicyFieldBaseProps {
  /** Spread of `form.getInputProps(path)`, or `{}` when there is no form. */
  inputProps: Record<string, any>;
  /** `rule.disabled` composed with `disabledWhen` and `ignoreReadOnly`. */
  disabled: boolean;
  /** Built from `rule.headerToggle` by the feature's `renderHeaderToggle`. */
  rightSection?: ReactNode;
  /** Props shared by every widget - label, disabled, data-pw, plus whatever the feature adds. */
  common: Record<string, unknown>;
  extra: TExtra;
}

export type WidgetRenderer<TExtra> = (ctx: WidgetContext<TExtra>) => ReactNode;

export interface PolicyFieldConfig<TExtra> {
  /** Keyed by `rule.widget`. The feature owns which components these are. */
  widgets: Record<string, WidgetRenderer<TExtra>>;
  /** Used when `rule.widget` matches no key. */
  fallbackWidget: string;
  /**
   * Wraps the rendered input - a grid column, a hint wrapper, or nothing.
   *
   * Feature-supplied because it does not generalise: FinalizeBatch and DeclarationView wrap fields
   * in `<EucdmCode>` (38 and 111 sites), TransitView uses it zero times.
   */
  layout?: (input: ReactNode, ctx: WidgetContext<TExtra>) => ReactNode;
  /**
   * Rendered into `rightSection` when `rule.headerToggle` is set.
   *
   * Takes `rule.disabled` composed with `ignoreReadOnly` ONLY - deliberately NOT `disabledWhen`,
   * which is very often derived from this same toggle's own value (e.g. "disable Country of
   * Dispatch when its header is off"). Disabling the switch on that condition would make it a
   * one-way control: turning it off would permanently disable the only thing that turns it back on.
   * Also takes the main field's own `dataPw`, so the toggle gets a stable id derived from it.
   */
  renderHeaderToggle?: (form: PolicyForm, togglePath: string, toggleValue: unknown, ctx: { disabled: boolean; dataPw?: string }) => ReactNode;
  /** Defaults to `{ label, disabled, 'data-pw' }`; override to add e.g. a size. */
  commonProps?: (ctx: { rule: FieldRule; disabled: boolean }) => Record<string, unknown>;
}

const defaultCommonProps = ({ rule, disabled }: { rule: FieldRule; disabled: boolean }) => ({
  label: rule.label,
  disabled,
  'data-pw': rule.dataPw,
});

/**
 * Builds a field renderer for a feature.
 *
 * The framework owns the structure that is the same everywhere - absent means render nothing,
 * disabled composes from three sources, a header toggle hangs off the rule, the widget is picked by
 * `rule.widget`. Everything visual is supplied by the feature, because it demonstrably differs:
 * the three call sites need different component sets and different wrappers.
 *
 * Imports only React and lodash on purpose. Reaching `@components` would pull its barrel, which
 * re-exports `HideElementFor` -> `useSettings` -> `window.location` at module load, and that would
 * make the whole framework unloadable outside a browser.
 */
export const createPolicyField = <TExtra extends object>(config: PolicyFieldConfig<TExtra>) => {
  const { widgets, fallbackWidget, layout, renderHeaderToggle, commonProps = defaultCommonProps } = config;

  const PolicyField: React.FC<PolicyFieldBaseProps & TExtra> = (props) => {
    const { path, rule, form, disabledWhen = false, hiddenWhen = false, ignoreReadOnly = false, ...rest } = props;

    if (!willRender({ rule, hiddenWhen })) {
      return null;
    }

    const disabled = (ignoreReadOnly ? false : rule.disabled) || disabledWhen;
    const inputProps: Record<string, any> = form ? form.getInputProps(path) : {};

    // Deliberately NOT the composed `disabled` above: `disabledWhen` is very often derived from
    // this SAME toggle's own value (e.g. "disable Country of Dispatch when its header is off") -
    // disabling the switch itself on that condition would make it a one-way control, since turning
    // it off would permanently disable the only thing that can turn it back on.
    const rightSection =
      rule.headerToggle && form && renderHeaderToggle
        ? renderHeaderToggle(form, rule.headerToggle, get(form.values, rule.headerToggle), {
            disabled: ignoreReadOnly ? false : rule.disabled,
            dataPw: rule.dataPw,
          })
        : undefined;

    const ctx: WidgetContext<TExtra> = {
      path,
      rule,
      form,
      disabledWhen,
      hiddenWhen,
      ignoreReadOnly,
      inputProps,
      disabled,
      rightSection,
      common: commonProps({ rule, disabled }),
      extra: rest as unknown as TExtra,
    };

    const render = widgets[rule.widget] ?? widgets[fallbackWidget];
    const input = render(ctx);

    return <>{layout ? layout(input, ctx) : input}</>;
  };

  return PolicyField;
};

/** Props every policy array field takes, regardless of feature. */
export interface PolicyArrayFieldBaseProps extends PolicyFieldBaseProps {
  /** Add-button text (e.g. "Add Container") - defaults to `List`'s own "Add". */
  addButtonLabel?: string;
}

export interface PolicyArrayListContext {
  /** The array's own resolved label (e.g. "Containers") - unlike an item's, never shown per row,
   *  so a feature that wants a header row above the list reads it from here, once. */
  label: string;
  /** The array's current items - `[]` when there is no form, same convention as `PolicyForm`. */
  items: unknown[];
  /** Whole-array replace, e.g. for add/remove - there is no per-item form path for this. */
  onChange: (items: unknown[]) => void;
  disabled: boolean;
  dataPw?: string;
  addButtonLabel?: string;
  createNewItem: () => unknown;
  /** Built from `rule.headerToggle` by the feature's `renderHeaderToggle`, same as `WidgetContext`'s
   *  own `rightSection` - `undefined` when the array has no `headerToggle` set, so a `renderList`
   *  that ignores this renders exactly as it did before headerToggle support existed. */
  headerToggle?: ReactNode;
  /** Renders one item's registered sub-fields, already resolved - the feature only supplies chrome
   *  (add/remove buttons, a Paper/Stack wrapper) around this, never the fields themselves. */
  renderItem: (item: unknown, index: number) => ReactNode;
}

export interface PolicyArrayFieldConfig<TExtra> extends Pick<PolicyFieldConfig<TExtra>, 'widgets' | 'fallbackWidget' | 'commonProps' | 'renderHeaderToggle'> {
  /**
   * Renders the array itself - add/remove chrome, one call per item field. Feature-supplied for the
   * same reason `layout` is: array editing UI (buttons, a Paper wrapper, spacing) differs per
   * feature, and the framework has no component library of its own to render with.
   */
  renderList: (ctx: PolicyArrayListContext) => ReactNode;
  /**
   * Wraps `renderList`'s WHOLE output - a grid column, same as `PolicyFieldConfig.layout` - never
   * called per item (an item that needs its own layout does that inside `renderList`/`renderItem`).
   */
  layout?: (input: ReactNode, ctx: Pick<PolicyArrayFieldBaseProps, 'path' | 'rule'> & { extra: TExtra }) => ReactNode;
}

/**
 * Builds an array-field renderer for a feature - the `createPolicyField` of arrays.
 *
 * An item's own present/disabled never varies by context (see `ItemFieldSpec`), so unlike
 * `PolicyField` there is no per-item `resolve()` here: every item renders the same registered
 * sub-fields, through the exact same `widgets`/`commonProps` a plain `<PolicyField>` uses (so a
 * `codes`/`text`/`number` sub-field looks identical to a top-level one), keyed on an indexed form
 * path (`${path}.${index}.${subPath}`) that Mantine's own `getInputProps`/`setFieldValue` already
 * understand natively - no per-field write-back through the array's own `onChange` is needed, only
 * add/remove (a whole-array replace) goes through it.
 *
 * Same import constraint as `createPolicyField`: React and lodash only, so this stays loadable in
 * the pure policy test suites. Array editing chrome is the feature's job via `renderList`.
 */
/**
 * Builds the `renderItem` function for one array field's current render - a closure over
 * everything an item sub-field needs (`renderNestedList`/`renderLeafField` are its two shapes),
 * factored out of `PolicyArrayField`'s own body purely to keep that body short.
 */
const makeItemFieldsRenderer = <TExtra extends object>(
  config: PolicyArrayFieldConfig<TExtra>,
  widgets: Record<string, WidgetRenderer<TExtra>>,
  fallbackWidget: string,
  commonProps: NonNullable<PolicyFieldConfig<TExtra>['commonProps']>,
  form: PolicyForm | undefined,
  rule: FieldRule,
  disabled: boolean,
  extra: TExtra,
) => {
  /** A nested `items` (e.g. a container's `seals`) recurses through `config.renderList` again. */
  const renderNestedList = (itemPath: string, index: number, label: string, spec: ItemFieldSpec<any>, dataPw?: string) => {
    const nestedItems = (form ? get(form.values, itemPath) : []) as unknown[];
    // A function closes over THIS index (e.g. the container's own) so a nested leaf's `dataPw`
    // can incorporate it - see `ItemFieldSpec.items`'s own doc comment.
    const nestedFields = typeof spec.items === 'function' ? spec.items(index) : (spec.items ?? {});

    return config.renderList({
      label,
      items: nestedItems ?? [],
      onChange: (newItems) => form?.setFieldValue(itemPath, newItems),
      disabled,
      dataPw,
      addButtonLabel: spec.addButtonLabel,
      createNewItem: spec.createNewItem ?? (() => ({})),
      renderItem: renderItemFields(itemPath, nestedFields),
    });
  };

  /** A plain leaf sub-field, rendered through the same `widgets`/`commonProps` a top-level field uses. */
  const renderLeafField = (itemPath: string, label: string, spec: ItemFieldSpec<any>, dataPw?: string) => {
    const subRule: FieldRule = {
      present: true,
      disabled,
      amendmentLocked: false,
      label,
      widget: spec.widget,
      code: spec.code,
      coerce: spec.coerce,
      // Inherited from the array's own resolved rule, not re-derived: `systems` is a
      // context-driven codelist filter (which sources a `code` list is looked up against), the
      // same for every item - there is no per-item context to derive a different one from.
      systems: rule.systems,
      dataPw,
    };
    const inputProps: Record<string, any> = form ? form.getInputProps(itemPath) : {};
    const ctx: WidgetContext<TExtra> = {
      path: itemPath,
      rule: subRule,
      form,
      disabledWhen: false,
      hiddenWhen: false,
      ignoreReadOnly: false,
      inputProps,
      disabled,
      rightSection: undefined,
      common: commonProps({ rule: subRule, disabled }),
      extra,
    };
    const render = widgets[spec.widget] ?? widgets[fallbackWidget];

    return render(ctx);
  };

  /**
   * Renders one item's registered sub-fields, keyed on ITS OWN indexed path underneath the
   * parent item's - `renderNestedList`/`renderLeafField` are the two shapes a sub-field can take.
   */
  const renderItemFields = (arrayPath: string, fields: Record<string, ItemFieldSpec<any>>) => (_item: unknown, index: number) =>
    Object.entries(fields).map(([subPath, spec]) => {
      const itemPath = `${arrayPath}.${index}.${subPath}`;
      const label = typeof spec.label === 'function' ? spec.label(index) : spec.label;
      const dataPw = typeof spec.dataPw === 'function' ? spec.dataPw(index) : (spec.dataPw ?? (rule.dataPw ? `${rule.dataPw}-${subPath}` : undefined));

      return (
        <Fragment key={subPath}>
          {spec.items ? renderNestedList(itemPath, index, label, spec, dataPw) : renderLeafField(itemPath, label, spec, dataPw)}
        </Fragment>
      );
    });

  return renderItemFields;
};

export const createPolicyArrayField = <TExtra extends object>(config: PolicyArrayFieldConfig<TExtra>) => {
  const { widgets, fallbackWidget, commonProps = defaultCommonProps, renderHeaderToggle } = config;

  const PolicyArrayField: React.FC<PolicyArrayFieldBaseProps & TExtra> = (props) => {
    const { path, rule, form, disabledWhen = false, hiddenWhen = false, ignoreReadOnly = false, addButtonLabel, ...rest } = props;

    if (!willRender({ rule, hiddenWhen })) {
      return null;
    }

    const disabled = (ignoreReadOnly ? false : rule.disabled) || disabledWhen;
    const items = (form ? get(form.values, path) : []) as unknown[];
    const extra = rest as unknown as TExtra;
    const renderItem = makeItemFieldsRenderer(config, widgets, fallbackWidget, commonProps, form, rule, disabled, extra)(path, rule.items ?? {});

    // Deliberately NOT the composed `disabled` above: `disabledWhen` is very often derived from
    // this SAME toggle's own value (e.g. containers' own `!containerHeader`) - see
    // `createPolicyField`'s identical comment on its own `rightSection`.
    const headerToggle =
      rule.headerToggle && form && renderHeaderToggle
        ? renderHeaderToggle(form, rule.headerToggle, get(form.values, rule.headerToggle), {
            disabled: ignoreReadOnly ? false : rule.disabled,
            dataPw: rule.dataPw,
          })
        : undefined;

    const list = config.renderList({
      label: rule.label,
      items: items ?? [],
      onChange: (newItems) => form?.setFieldValue(path, newItems),
      disabled,
      dataPw: rule.dataPw,
      addButtonLabel,
      createNewItem: rule.createNewItem ?? (() => ({})),
      headerToggle,
      renderItem,
    });

    return <>{config.layout ? config.layout(list, { path, rule, extra }) : list}</>;
  };

  return PolicyArrayField;
};
